import { computed, inject } from '@angular/core'
import { tapResponse } from '@ngrx/operators'
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals'
import { removeEntity, setAllEntities, updateEntity, withEntities } from '@ngrx/signals/entities'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { ToastrService } from 'ngx-toastr'
import { forkJoin, pipe, switchMap } from 'rxjs'
import { PostService } from 'src/app/core/services/posts/post.service'
import { StaffService } from 'src/app/core/services/staff/staff.service'
import { PaginatedList } from 'src/app/shared/interfaces/api-response/api-paginated-list.interface'
import { PostFilter } from 'src/app/shared/interfaces/post/post-filter.interface'
import { PostListItem } from 'src/app/shared/interfaces/post/post-list-item.interface'
import { PostType } from 'src/app/shared/interfaces/post/post-type.interface'
import { StaffListItem } from 'src/app/shared/interfaces/staff/staff-list-item.interface'
import { SignalRService } from 'src/app/core/services/signalr-service'
import { ChangeNotification, EntityType } from 'src/app/shared/signalr/change-notification.interface'
import { PostUpdateEvent } from 'src/app/shared/signalr/post-update-event.interface'
import { PostCounter } from 'src/app/shared/signalr/post-counter.interface'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

interface PostsState {
    postsPaged: PaginatedList<PostListItem> | null
    postCounter: PostCounter
    filter: PostFilter
    postTypes: PostType[]
    staff: StaffListItem[]
    newPostReceivedCounter: number
}

const initialState: PostsState = {
    postsPaged: null,
    postCounter: {
        postsToProcessCount: 0,
        urgentPostsToProcessCount: 0,
    },
    filter: {
        pageNumber: 1,
        pageSize: 60,
        sortColumn: 'createdDate',
        isProcessed: false,
    },
    postTypes: [],
    staff: [],
    newPostReceivedCounter: 0,
}

export const PostsStore = signalStore(
    {
        providedIn: 'root',
        protectedState: false,
    },
    withState(initialState),
    withEntities<PostListItem>(),
    withComputed((store) => ({
        postListPostTypes: computed(() =>
            store
                .postTypes()
                .filter((type) => type.isPostList)
                .sort((a, b) => a.name.localeCompare(b.name))
        ),
    })),
    withMethods((store) => {
        const postService = inject(PostService)
        const signalRService = inject(SignalRService)
        const staffService = inject(StaffService)
        const toastr = inject(ToastrService)

        return {
            changePage(page: number): void {
                patchState(store, { filter: { ...store.filter(), pageNumber: page } })
            },
            deletePost: rxMethod<number>(
                pipe(
                    switchMap((id) =>
                        postService.deletePost(id).pipe(
                            tapResponse({
                                next: () => {
                                    toastr.success('Post deleted successfully')
                                    patchState(store, removeEntity(id))
                                },
                                error: (error) => {
                                    toastr.error('Error deleting post')
                                },
                            })
                        )
                    )
                )
            ),
            removePost: rxMethod<number>(
                pipe(
                    switchMap((id) =>
                        postService.removePost(id).pipe(
                            tapResponse({
                                next: () => {
                                    toastr.success('Post removed successfully')
                                    patchState(store, removeEntity(id))
                                },
                                error: (error) => {
                                    toastr.error('Error removing post')
                                },
                            })
                        )
                    )
                )
            ),
            setPostVoid: rxMethod<{ postId: number; isVoid: boolean }>(
                pipe(
                    switchMap(({ postId, isVoid }) => {
                        const setVoid = isVoid ? postService.voidPost(postId) : postService.unvoidPost(postId)

                        return setVoid.pipe(
                            tapResponse({
                                next: () => {
                                    toastr.success(`Post ${isVoid ? 'Voided' : 'Unvoided'}`, 'Success')
                                    patchState(store, removeEntity(postId))
                                },
                                error: (error) => {
                                    toastr.error(`Error ${isVoid ? 'voiding' : 'unvoiding'} post`)
                                },
                            })
                        )
                    })
                )
            ),
            assignPostToStaffId: rxMethod<{ postId: number; staffId: number }>(
                pipe(
                    switchMap(({ postId, staffId }) =>
                        postService.assignPostToStaff(postId, staffId).pipe(
                            tapResponse({
                                next: () => {
                                    const staff = store.staff().find((s) => s.id === staffId)
                                    if (staff) {
                                        patchState(
                                            store,
                                            updateEntity({
                                                id: postId,
                                                changes: {
                                                    userInChargeId: staffId,
                                                    userInChargeName: staff.fullName,
                                                    imInCharge: staff.isMe,
                                                },
                                            })
                                        )
                                    }
                                    if (staff?.isMe) {
                                        toastr.success('Post taken successfully')
                                    } else {
                                        toastr.success(`Post assigned to ${staff?.fullName} successfully`)
                                    }
                                },
                                error: (error) => {
                                    toastr.error('Error assigning post to staff')
                                },
                            })
                        )
                    )
                )
            ),
            unassignPost: rxMethod<number>(
                pipe(
                    switchMap((id) =>
                        postService.unassignPost(id).pipe(
                            tapResponse({
                                next: () => {
                                    patchState(
                                        store,
                                        updateEntity({
                                            id,
                                            changes: {
                                                userInChargeId: null,
                                                userInChargeName: null,
                                                imInCharge: false,
                                            },
                                        })
                                    )
                                    toastr.success('Post unassigned successfully')
                                },
                                error: (error) => {
                                    toastr.error('Error unassigning post')
                                },
                            })
                        )
                    )
                )
            ),
            updateFilter(filter: PostFilter): void {
                patchState(store, { filter })
            },
            load: rxMethod<PostFilter>(
                pipe(
                    switchMap((filter) =>
                        postService.getPostsPaged(filter).pipe(
                            tapResponse({
                                next: (postsPaged) => {
                                    patchState(store, setAllEntities(postsPaged.items))
                                    patchState(store, { postsPaged: postsPaged, newPostReceivedCounter: 0 })
                                },
                                error: (error) => {
                                    toastr.error('Error loading posts')
                                },
                            })
                        )
                    )
                )
            ),
            startSubscribing(): void {
                signalRService
                    .getChangesFor<PostUpdateEvent>(EntityType.Post)
                    .pipe(takeUntilDestroyed())
                    .subscribe((change: ChangeNotification<PostUpdateEvent>) => {
                        const eventPost = change.data
                        switch (change.operation.toLowerCase()) {
                            case 'insert':
                                patchState(store, { newPostReceivedCounter: store.newPostReceivedCounter() + 1 })
                                break
                            case 'update':
                                const inChargeUser = store.staff().find((x) => x.id === eventPost.userInChargeId)

                                patchState(
                                    store,
                                    updateEntity({
                                        id: eventPost.id,
                                        changes: {
                                            userInChargeId: eventPost.userInChargeId,
                                            userInChargeName: inChargeUser?.fullName,
                                            isVoided: eventPost.isVoid,
                                            isProcessed: eventPost.isProcessed,
                                        },
                                    })
                                )
                                break
                            case 'delete':
                                patchState(store, removeEntity(eventPost.id))
                                break
                            default:
                                break
                        }

                        if (eventPost.postCounter) {
                            patchState(store, { postCounter: eventPost.postCounter })
                        }
                    })
            },
            loadInitialData: rxMethod<void>(
                pipe(
                    switchMap(() => {
                        const postTypes = postService.getPostTypes()
                        const staff = staffService.getAllStaff()

                        return forkJoin([postTypes, staff]).pipe(
                            tapResponse({
                                next: ([postTypesResponse, staffResponse]) => {
                                    patchState(store, { postTypes: postTypesResponse, staff: staffResponse })
                                },
                                error: (error) => {
                                    toastr.error('Error loading initial data')
                                },
                            })
                        )
                    })
                )
            ),
        }
    }),
    withHooks({
        onInit(store) {
            store.startSubscribing()
            store.load(store.filter)
            store.loadInitialData()
        },
    })
)
